/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	TOOLTIP_CONTENT_SETTINGS,
	TOOLTIP_CONTENT_MODAL_CONTENT,
	UICONTROL_TEXT_CANCEL,
	UICONTROL_TEXT_SAVE,
	TOOLTIP_CONTENT_SETTINGS_CUSTOM_FIELDS_RADIO_ALL,
	TOOLTIP_CONTENT_SETTINGS_CUSTOM_FIELDS_RADIO_SELECT,
} from '@touchplan/strings';
import { BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/services/app-state.service';
import {
	HoverOverUserPreferencesService,
	IHoverOverUserPreferences,
} from 'src/app/services/hover-over-preferences.service';
import { PlanState } from 'src/app/services/plan-state.service';
import {
	ChecklistDataMap,
	IChecklistItemDataWithId,
} from 'src/app/shared/ui/material/checkbox-list/checkbox-list-model';
import { CONFIGURATION_LIST, IHoverOverContentOption } from './hover-over-content-configuration-list';
import { HoverOverContentEditorComponent } from './hover-over-content-editor/hover-over-content-editor.component';
import { analyticsEventsService } from '../../../plain-javascript/services/analytics/AnalyticsEventsService';

@Component({
	selector: 'hover-over-content-starter',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoverOverContentStarterComponent implements OnInit, OnDestroy {
	private shutdownSubject$: Subject<boolean> = new Subject();
	private tooltipContentOptions: IHoverOverContentOption[] = CONFIGURATION_LIST;
	private userId: string;

	private tooltipContentOptions$: BehaviorSubject<IHoverOverContentOption[]> = new BehaviorSubject<
		IHoverOverContentOption[]
	>(this.tooltipContentOptions);

	// Triggered when the dialog is opened
	@Output() public afterOpened: EventEmitter<void> = new EventEmitter();

	// Triggered when the dialog is closed
	@Output() public afterClosed: EventEmitter<void> = new EventEmitter();

	constructor(
		private dialog: MatDialog,
		private planState: PlanState,
		private appState: AppState,
		private hoverOverUserPreferencesService: HoverOverUserPreferencesService
	) {}

	public ngOnInit(): void {
		this.getUserId();
		this.openTooltipContentDialog();
	}

	public openTooltipContentDialog(): void {
		this.afterOpened.next();
		const data = {
			title: TOOLTIP_CONTENT_SETTINGS,
			content: [
				TOOLTIP_CONTENT_MODAL_CONTENT,
				TOOLTIP_CONTENT_SETTINGS_CUSTOM_FIELDS_RADIO_ALL,
				TOOLTIP_CONTENT_SETTINGS_CUSTOM_FIELDS_RADIO_SELECT,
			],
			dataObservable: combineLatest([this.getDataObservable(), this.getCustomFieldOptionsObservable()]).pipe(
				first(),
				map(([hoverOverStaticSettings, hoverOverCustomFieldsSettings]) => [
					...hoverOverStaticSettings,
					hoverOverCustomFieldsSettings,
				])
			),
			yesButtonText: UICONTROL_TEXT_SAVE.toUpperCase(),
			noButtonText: UICONTROL_TEXT_CANCEL.toUpperCase(),
		};

		this.dialog
			.open(HoverOverContentEditorComponent, { data, disableClose: true })
			.afterClosed()
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$))
			.subscribe((saveObj) => {
				this.afterClosed.next();
				if (saveObj) {
					this.hoverOverUserPreferencesService.updateHoverOverUserProjectPreferences(
						this.userId,
						this.planState.projectId,
						this.mapHoverOverSettings(saveObj)
					);

					this.saveCustomFieldsHoverOverPreferences(saveObj.customFieldsPreferences);
					analyticsEventsService.setHoverConfig();
				}
			});
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}

	private getUserId(): void {
		this.appState.userId.pipe(takeUntil(this.shutdownSubject$)).subscribe((userId) => (this.userId = userId));
	}

	private getDataObservable(): Observable<[IHoverOverContentOption[], Partial<IHoverOverUserPreferences>]> {
		return combineLatest([
			this.tooltipContentOptions$,
			this.hoverOverUserPreferencesService.hoverOverUserProjectPreferencesChanges$(
				this.userId,
				this.planState.projectId
			),
		]);
	}

	private mapHoverOverSettings(saveObj: {
		staticHoverPreferences: ChecklistDataMap;
	}): Partial<IHoverOverUserPreferences> {
		const value = saveObj.staticHoverPreferences;
		return {
			isEnabled: true,
			displayDescription: !!value.get('description')?.isChecked,
			displayRole: !!value.get('role')?.isChecked,
			displayLocation: !!value.get('location')?.isChecked,
			displayDuration: !!value.get('duration')?.isChecked,
			displayCrew: !!value.get('crew')?.isChecked,
			displayAssignTo: !!value.get('assignTo')?.isChecked,
			displayNeedByDate: !!value.get('needByDate')?.isChecked,
			displayImageAttachmentsCount: !!value.get('imageAttachmentsCount')?.isChecked,
			displayAllCustomFields: !!value.get('allCustomFields')?.isChecked,
		};
	}

	private getCustomFieldOptionsObservable(): Observable<IChecklistItemDataWithId[]> {
		return this.planState.customFieldsApi.getCustomFieldDefinitionsEnabled().pipe(
			switchMap((customFields) =>
				this.getHoverOverUserCustomFieldOptions().pipe(
					map((enabledCustomFields) => {
						return customFields.map((customField) => ({
							...customField,
							isChecked: enabledCustomFields?.includes(customField.id) ?? false,
						}));
					})
				)
			)
		);
	}

	private getHoverOverUserCustomFieldOptions(): Observable<string[]> {
		return this.hoverOverUserPreferencesService.hoverOverUserProjectEnabledCustomFieldsChanges$(
			this.userId,
			this.planState.projectId
		);
	}

	private saveCustomFieldsHoverOverPreferences(customFieldsPreferences: ChecklistDataMap): void {
		const enabledCustomFieldIds = [];

		customFieldsPreferences.forEach((value, key) => {
			if (value.isChecked) {
				enabledCustomFieldIds.push(key);
			}
		});

		this.hoverOverUserPreferencesService.setHoverOverUserProjectEnabledCustomFields(
			this.userId,
			this.planState.projectId,
			enabledCustomFieldIds
		);
	}
}
